import React from 'react';
import PropTypes from 'prop-types';
import { FormText, FormFeedback, FormGroup, Label } from 'reactstrap';

const BaseInput = (props) => {
  const {
    children,
    childImplementsOwnLabel,
    errorMessage,
    successMessage,
    helpText,
    hideLabel,
    id,
    label,
    onChange,
    busy,
    ...rest
  } = props;
  const errorId = `${id}_error`;
  const successId = `${id}_success`;
  const helpTextId = `${id}_help`;
  const describedBy = [
    (errorMessage ? errorId : null),
    (successMessage ? successId : null),
    (helpText ? helpTextId : null),
  ].filter(Boolean).join(' ') || null;
  const childProps = {
    ...rest,
    id,
    label,
    onChange,
    valid: !!successMessage,
    invalid: !!errorMessage,
    'aria-describedby': describedBy,
    'aria-label': label,
  };
  const control = typeof children === 'function'
    ? children(childProps)
    : React.cloneElement(children, childProps);
  // The wrapper is rendered unconditionally so that toggling `busy` does not
  // produce a childList mutation around the <input>. Password managers
  // (e.g. LastPass) tear down their inline icon when the input is re-parented,
  // which causes the icon to jump to the left on the next render. Only the
  // modifier class changes when busy toggles, which is attribute-only.
  const wrappedControl = (
    <div
      className={`email-field-input-wrap${busy ? ' email-field-input-wrap--busy' : ''}`}
    >
      {control}
    </div>
  );
  return (
    <FormGroup>
      {!childImplementsOwnLabel && (
        <Label hidden={hideLabel} htmlFor={id}>
          {label}
        </Label>
      )}
      {wrappedControl}
      {errorMessage && (
        <FormFeedback
          className="d-block"
          id={errorId}
          valid={false}
        >
          {errorMessage}
        </FormFeedback>
      )}
      {successMessage && (
        <FormFeedback
          valid
          className="d-block"
          id={successId}
        >
          {successMessage}
        </FormFeedback>
      )}
      {helpText && (
        <FormText id={helpTextId}>{helpText}</FormText>
      )}
    </FormGroup>
  );
};

BaseInput.childPropTypes = {
  errorMessage: PropTypes.string,
  successMessage: PropTypes.string,
  helpText: PropTypes.string,
  hideLabel: PropTypes.bool,
  id: PropTypes.string.isRequired,
  label: PropTypes.string.isRequired,
  onChange: PropTypes.func,
  busy: PropTypes.bool,
};

BaseInput.propTypes = {
  ...BaseInput.childPropTypes,
  children: PropTypes.oneOfType([PropTypes.node, PropTypes.func]),
};

BaseInput.defaultChildProps = {
  errorMessage: null,
  successMessage: null,
  helpText: null,
  hideLabel: false,
  onChange() {},
  busy: false,
};

BaseInput.defaultProps = {
  children: null,
  childImplementsOwnLabel: false,
  ...BaseInput.defaultChildProps,
};

export default BaseInput;
